import React from "react";

import { Column } from "primereact/column";
import { PaginatorPageChangeEvent } from "primereact/paginator";

import * as Styled from "./sectioned-data-table.styled";
import {
  DEFAULT_TABLE_ROWS_PER_PAGE,
  DEFAULT_TABLE_ROWS_PER_PAGE_OPTIONS,
} from "../../../shared/ui/data-table/model/data-table-contract";
import type { IProblemRecord } from "../../../shared/ui/data-table/model/data-table-contract";
import {
  TablePaginationHeaderControls,
  TablePaginatorFooter,
} from "../../../shared/ui/data-table/ui/table-paginator-footer";
import { renderTableSortIcon } from "../../../shared/ui/data-table/ui/table-sort-icon";
import { ROUTES } from "../../../system/router/routes";
import { useParams } from "react-router-dom";
import { SectionSidebarNav } from "../../../shared/ui/section-sidebar-nav/section-sidebar-nav";

type TableRow = Record<string, unknown>;
// eslint-disable-next-line no-unused-vars
type TableRowAction = (row: TableRow) => void;

interface TablePaginationState {
  first: number;
  rows: number;
}

export const SectionedDataTable = ({
  healthEntity,
  headerAction,
  onRowClick,
  onRoiViewRequest,
  onRoiDownloadRequest,
  onRoiDuplicateRequest,
  onRoiCancelRequest,
  onRoiEditRequest,
  useSharedSectionNav = false,
  scrollContainerRef,
}: {
  healthEntity: IProblemRecord;
  headerAction?: React.ReactNode;
  onRowClick?: TableRowAction;
  onRoiViewRequest?: TableRowAction;
  onRoiDownloadRequest?: TableRowAction;
  onRoiDuplicateRequest?: TableRowAction;
  onRoiCancelRequest?: TableRowAction;
  onRoiEditRequest?: TableRowAction;
  useSharedSectionNav?: boolean;
  scrollContainerRef?: { current: HTMLElement | null };
}) => {
  const [tablePaginationState, setTablePaginationState] = React.useState<
    Record<string, TablePaginationState>
  >({});

  const isUrl = (value: unknown): value is string => {
    if (typeof value !== "string") return false;

    if (!value.includes(".") && !value.startsWith("http")) return false;

    try {
      const url = new URL(
        value.startsWith("http") ? value : `https://${value}`,
      );
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  const patientID = useParams().patientID;

  const isRowClickable = React.useCallback(
    (row: Record<string, unknown>) =>
      !("roiStatus" in row) || row.roiStatus === "Completed",
    [],
  );

  const navigationSections = React.useMemo(
    () =>
      (healthEntity?.subnav ?? []).map((item) => ({
        id: item.sectionId,
        label: item.label,
      })),
    [healthEntity?.subnav],
  );

  if (!patientID) return null;

  const ROI_STATUS_COLUMN_WIDTH = 130;
  const ROI_ACTIONS_COLUMN_WIDTH = 152;

  const getFrozenColumnWidth = (key: string) => {
    if (key === "roiStatus") {
      return ROI_STATUS_COLUMN_WIDTH;
    }

    if (key === "roiActions") {
      return ROI_ACTIONS_COLUMN_WIDTH;
    }

    return null;
  };

  const getBaseColumnStyle = (width?: number | string) => {
    if (width === undefined) {
      return undefined;
    }

    const cssWidth = typeof width === "number" ? `${width}px` : width;

    return {
      width: cssWidth,
      minWidth: cssWidth,
      maxWidth: cssWidth,
    };
  };

  const getColumnClassName = (key: string) => {
    if (key === "roiStatus") {
      return "is-roi-status-column";
    }

    if (key === "roiActions") {
      return "is-roi-actions-column";
    }

    return undefined;
  };

  const getRowsPerPageOptions = (
    configuredRowsPerPage: number | undefined,
    configuredOptions: number[] | undefined,
  ) => {
    const baseOptions =
      configuredOptions && configuredOptions.length > 0
        ? configuredOptions
        : Array.from(DEFAULT_TABLE_ROWS_PER_PAGE_OPTIONS);

    if (
      configuredRowsPerPage === undefined ||
      baseOptions.includes(configuredRowsPerPage)
    ) {
      return baseOptions;
    }

    return [configuredRowsPerPage, ...baseOptions];
  };

  const tableContent =
    healthEntity?.sections && healthEntity.sections.length > 0 ? (
      <Styled.EntityContainer>
        {healthEntity.sections.map((section) => (
          <Styled.Entity key={section.id}>
            {healthEntity.sections.length > 1 && (
              <Styled.EntityTitle id={section.id}>
                {section.title}
              </Styled.EntityTitle>
            )}
            {/* TABLES */}
            {section.tables.length > 0 ? (
              section.tables?.map((table, tableIndex) => {
                const currentRowsPerPage =
                  table.ui?.defaultRowsPerPage ?? DEFAULT_TABLE_ROWS_PER_PAGE;
                const rowsPerPageOptions = getRowsPerPageOptions(
                  currentRowsPerPage,
                  table.ui?.rowsPerPageOptions,
                );
                const paginationState = tablePaginationState[table.id];
                const activeRowsPerPage =
                  paginationState?.rows ?? currentRowsPerPage;
                const maxFirst =
                  table.rows.length > 0
                    ? Math.floor((table.rows.length - 1) / activeRowsPerPage) *
                      activeRowsPerPage
                    : 0;
                const activeFirst = Math.min(
                  paginationState?.first ?? 0,
                  maxFirst,
                );
                const visibleRows = table.rows.slice(
                  activeFirst,
                  activeFirst + activeRowsPerPage,
                );

                  return (
                    <Styled.TableWrapper key={table.id}>
                      <Styled.TableActions>
                        <Styled.TableActionGroup>
                          <TablePaginationHeaderControls
                            first={activeFirst}
                            rows={activeRowsPerPage}
                            totalRecords={table.rows.length}
                            rowsPerPageOptions={rowsPerPageOptions}
                            onRowsChange={(rows) => {
                              setTablePaginationState((current) => ({
                                ...current,
                                [table.id]: {
                                  first: 0,
                                  rows,
                                },
                              }));
                            }}
                          />
                        </Styled.TableActionGroup>
                        {headerAction && tableIndex === 0 ? (
                          <Styled.TableActionGroup>
                            {headerAction}
                          </Styled.TableActionGroup>
                        ) : null}
                      </Styled.TableActions>
                    <Styled.Table
                      value={visibleRows}
                      scrollable
                      removableSort
                      sortIcon={renderTableSortIcon}
                      tableStyle={{ minWidth: "100%" }}
                      emptyMessage="No records found."
                      rowClassName={
                        onRowClick
                          ? (row) =>
                              isRowClickable(row) ? "is-clickable-row" : ""
                          : undefined
                      }
                      onRowClick={
                        onRowClick
                          ? (event) => {
                              if (isRowClickable(event.data)) {
                                onRowClick(event.data);
                              }
                            }
                          : undefined
                      }
                    >
                      {table.columns.map((c) => {
                        const frozenWidth = getFrozenColumnWidth(c.key);
                        const isFrozenRightColumn = frozenWidth !== null;
                        const columnClassName = getColumnClassName(c.key);
                        const columnStyle = isFrozenRightColumn
                          ? getBaseColumnStyle(frozenWidth)
                          : getBaseColumnStyle(c.width);

                        return (
                          <Column
                            key={c.key}
                            field={c.key}
                            header={c.header}
                            sortable={!!c.sortable}
                            frozen={isFrozenRightColumn}
                            alignFrozen={
                              isFrozenRightColumn ? "right" : undefined
                            }
                            style={columnStyle}
                            headerStyle={columnStyle}
                            bodyClassName={columnClassName}
                            headerClassName={columnClassName}
                            body={(row) => {
                              const v = row?.[c.key];

                              switch (c.key) {
                                case "roiActions":
                                  if (row.roiStatus === "Draft") {
                                    return (
                                      <Styled.RoiControls>
                                        <Styled.RoiButton
                                          type="button"
                                          aria-label="Cancel request"
                                          title="Cancel request"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            onRoiCancelRequest?.(row);
                                          }}
                                        >
                                          <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <g clipPath="url(#clip0_1606_7091)">
                                              <path
                                                d="M12.9167 12.9163L7.08397 7.08301M7.08459 12.9163L12.9173 7.08301"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M18.3333 10.0003C18.3333 5.39795 14.6023 1.66699 10 1.66699C5.39763 1.66699 1.66667 5.39795 1.66667 10.0003C1.66667 14.6027 5.39763 18.3337 10 18.3337C14.6023 18.3337 18.3333 14.6027 18.3333 10.0003Z"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                            </g>
                                            <defs>
                                              <clipPath id="clip0_1606_7091">
                                                <rect
                                                  width="20"
                                                  height="20"
                                                  fill="white"
                                                />
                                              </clipPath>
                                            </defs>
                                          </svg>
                                        </Styled.RoiButton>
                                        <Styled.RoiButton
                                          type="button"
                                          aria-label="Edit request"
                                          title="Edit request"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            onRoiEditRequest?.(row);
                                          }}
                                        >
                                          <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <g clipPath="url(#clip0_1606_8207)">
                                              <path
                                                d="M11.7281 3.2382C12.3491 2.56539 12.6596 2.22899 12.9895 2.03277C13.7856 1.55929 14.7659 1.54457 15.5753 1.99393C15.9107 2.18016 16.2307 2.50709 16.8708 3.16096C17.5109 3.81483 17.831 4.14176 18.0132 4.48443C18.4532 5.31126 18.4387 6.31265 17.9752 7.12591C17.7832 7.46296 17.4538 7.78014 16.7952 8.41449L8.95883 15.9622C7.71073 17.1644 7.08667 17.7655 6.30672 18.0701C5.52677 18.3747 4.66933 18.3523 2.95447 18.3075L2.72115 18.3014C2.19909 18.2877 1.93806 18.2809 1.78632 18.1087C1.63458 17.9365 1.6553 17.6706 1.69673 17.1388L1.71923 16.8501C1.83584 15.3532 1.89414 14.6049 2.18642 13.9322C2.4787 13.2594 2.98287 12.7132 3.99119 11.6207L11.7281 3.2382Z"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M10.8333 3.33301L16.6667 9.16634"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinejoin="round"
                                              />
                                            </g>
                                            <defs>
                                              <clipPath id="clip0_1606_8207">
                                                <rect
                                                  width="20"
                                                  height="20"
                                                  fill="white"
                                                />
                                              </clipPath>
                                            </defs>
                                          </svg>
                                        </Styled.RoiButton>
                                      </Styled.RoiControls>
                                    );
                                  }

                                  if (row.roiStatus === "Completed") {
                                    return (
                                      <Styled.RoiControls>
                                        <Styled.RoiButton
                                          type="button"
                                          aria-label="Duplicate request"
                                          title="Duplicate request"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            onRoiDuplicateRequest?.(row);
                                          }}
                                        >
                                          <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            aria-hidden="true"
                                          >
                                            <g clipPath="url(#clip0_1967_17193)">
                                              <path
                                                d="M9.16667 18.3331C8.24619 18.3331 7.5 17.587 7.5 16.6665V9.16764C7.5 8.24671 8.24691 7.50033 9.16783 7.50098L16.6678 7.50627C17.5878 7.50692 18.3333 8.25292 18.3333 9.17297V16.6665C18.3333 17.587 17.5872 18.3331 16.6667 18.3331H9.16667Z"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                              />
                                              <path
                                                d="M14.166 7.02394V3.34103C14.166 2.421 13.4205 1.675 12.5005 1.67436L3.33385 1.66797C2.41292 1.66733 1.66602 2.4137 1.66602 3.33464V12.5001C1.66602 13.4206 2.41221 14.1668 3.33268 14.1668H7.02316"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                                strokeLinejoin="round"
                                              />
                                            </g>
                                            <defs>
                                              <clipPath id="clip0_1967_17193">
                                                <rect
                                                  width="20"
                                                  height="20"
                                                  fill="white"
                                                />
                                              </clipPath>
                                            </defs>
                                          </svg>
                                        </Styled.RoiButton>
                                        <Styled.RoiButton
                                          type="button"
                                          aria-label="View request"
                                          title="View request"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            onRoiViewRequest?.(row);
                                          }}
                                        >
                                          <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M17.9541 9.20448C18.2075 9.55975 18.3341 9.73738 18.3341 10.0003C18.3341 10.2633 18.2075 10.4409 17.9541 10.7962C16.8157 12.3925 13.9085 15.8337 10.0008 15.8337C6.09313 15.8337 3.18589 12.3925 2.04751 10.7962C1.79416 10.4409 1.66748 10.2633 1.66748 10.0003C1.66748 9.73738 1.79416 9.55975 2.04751 9.20448C3.18589 7.60819 6.09313 4.16699 10.0008 4.16699C13.9085 4.16699 16.8157 7.60819 17.9541 9.20448Z"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                            />
                                            <path
                                              d="M12.5005 10C12.5005 8.61929 11.3812 7.5 10.0005 7.5C8.61978 7.5 7.50049 8.61929 7.50049 10C7.50049 11.3807 8.61978 12.5 10.0005 12.5C11.3812 12.5 12.5005 11.3807 12.5005 10Z"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                            />
                                          </svg>
                                        </Styled.RoiButton>
                                        <Styled.RoiButton
                                          type="button"
                                          aria-label="Download request"
                                          title="Download request"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            onRoiDownloadRequest?.(row);
                                          }}
                                        >
                                          <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M17.4999 14.167V16.2503C17.4999 16.9407 16.9403 17.5003 16.2499 17.5003H3.74995C3.05959 17.5003 2.49995 16.9407 2.49995 16.2503V14.167"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                            <path
                                              d="M6.25 9.58333L10 13.3333L13.75 9.58333M10 12.5V2.5"
                                              stroke="currentColor"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </Styled.RoiButton>
                                      </Styled.RoiControls>
                                    );
                                  }

                                  if (row.roiStatus === "Cancelled") {
                                    return <> </>;
                                  }
                                  break;
                                case "roiStatus":
                                  return (
                                    <Styled.RoiStatus status={v}>
                                      {v}
                                    </Styled.RoiStatus>
                                  );
                                case "encDetails":
                                  return (
                                    <Styled.DetailsLink
                                      to={ROUTES.PATIENT_DETAILS_ENCOUNTERS_HISTORY_REPORT.replace(
                                        ":patientID",
                                        patientID,
                                      ).replace(":encounterID", row.encCode)}
                                    >
                                      History & Physical Report
                                    </Styled.DetailsLink>
                                  );
                                case "encLabsProcedures":
                                  return (
                                    <Styled.DetailsLink
                                      to={ROUTES.PATIENT_DETAILS_ENCOUNTERS_LABS_REPORT.replace(
                                        ":patientID",
                                        patientID,
                                      ).replace(":encounterID", row.encCode)}
                                    >
                                      Labs & Procedures
                                    </Styled.DetailsLink>
                                  );
                                case "encBilling":
                                  return (
                                    <Styled.DetailsLink
                                      to={ROUTES.PATIENT_DETAILS_ENCOUNTERS_BILLING_REPORT.replace(
                                        ":patientID",
                                        patientID,
                                      ).replace(":encounterID", row.encCode)}
                                    >
                                      Billing Information
                                    </Styled.DetailsLink>
                                  );
                                case "encScannedDocs":
                                  return (
                                    <Styled.DetailsLink
                                      to={ROUTES.PATIENT_DETAILS_ENCOUNTERS_SCANNED_DOCS.replace(
                                        ":patientID",
                                        patientID,
                                      ).replace(":encounterID", row.encCode)}
                                    >
                                      Scanned Documents
                                    </Styled.DetailsLink>
                                  );
                                case "docUrl":
                                  if (isUrl(v)) {
                                    const href = v.startsWith("http")
                                      ? v
                                      : `https://${v}`;
                                    return (
                                      <Styled.DocLink
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <svg
                                          width="20"
                                          height="20"
                                          viewBox="0 0 20 20"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <path
                                            d="M17.9541 9.20417C18.2075 9.55944 18.3341 9.73707 18.3341 10C18.3341 10.263 18.2075 10.4406 17.9541 10.7959C16.8157 12.3922 13.9085 15.8334 10.0008 15.8334C6.09313 15.8334 3.18589 12.3922 2.04751 10.7959C1.79416 10.4406 1.66748 10.263 1.66748 10C1.66748 9.73707 1.79416 9.55944 2.04751 9.20417C3.18589 7.60789 6.09313 4.16669 10.0008 4.16669C13.9085 4.16669 16.8157 7.60789 17.9541 9.20417Z"
                                            stroke="#065FA6"
                                            strokeWidth="1.5"
                                          />
                                          <path
                                            d="M12.5005 10C12.5005 8.61929 11.3812 7.5 10.0005 7.5C8.61978 7.5 7.50049 8.61929 7.50049 10C7.50049 11.3807 8.61978 12.5 10.0005 12.5C11.3812 12.5 12.5005 11.3807 12.5005 10Z"
                                            stroke="#065FA6"
                                            strokeWidth="1.5"
                                          />
                                        </svg>
                                        View Document
                                      </Styled.DocLink>
                                    );
                                  }
                                  break;
                                default:
                                  return v ?? "—";
                              }
                            }}
                          />
                        );
                      })}
                    </Styled.Table>
                    <TablePaginatorFooter
                      first={activeFirst}
                      rows={activeRowsPerPage}
                      totalRecords={table.rows.length}
                      rowsPerPageOptions={rowsPerPageOptions}
                      onPageChange={(event: PaginatorPageChangeEvent) => {
                        setTablePaginationState((current) => ({
                          ...current,
                          [table.id]: {
                            first: event.first,
                            rows: event.rows,
                          },
                        }));
                      }}
                    />
                  </Styled.TableWrapper>
                );
              })
            ) : (
              <Styled.NoData>No data available</Styled.NoData>
            )}
          </Styled.Entity>
        ))}
      </Styled.EntityContainer>
    ) : null;

  return (
    <Styled.InnerContainer>
      {navigationSections.length > 0 &&
        (useSharedSectionNav ? (
          <SectionSidebarNav
            ariaLabel="Section navigation"
            sections={navigationSections}
            scrollContainerRef={scrollContainerRef}
          />
        ) : (
          <Styled.Submenu>
            {healthEntity.subnav?.map((item) => (
              <a key={item.sectionId} href={`#${item.sectionId}`}>
                {item.label}
              </a>
            ))}
          </Styled.Submenu>
        ))}
      <Styled.EntityScroller
        ref={scrollContainerRef as React.RefObject<HTMLDivElement>}
      >
        {tableContent}
      </Styled.EntityScroller>
    </Styled.InnerContainer>
  );
};
